import type { SemaineAgenda } from '../partage/api/types';
import { decaler } from '../partage/datesEnMots';

/**
 * Les règles de l'accueil, sans rendu (chantier `navigation`, 02, maquette du carnet
 * `accueil.html`). Le serveur calcule tout ; ici, on place les galets en cercle, on trie et on
 * coupe à trois lignes, rien de plus.
 */

/** Trois lignes au plus par bloc. */
export const LIGNES_PAR_BLOC = 3;

/** Au-delà, les galets se font plus petits pour tenir sans se chevaucher. */
export const GALETS_AVANT_DE_SERRER = 6;

/** Les deux rayons du cercle, en part de la carte : assez loin de « Toi », assez dedans pour le galet. */
const RAYON_EN_LARGEUR = 0.35;
const RAYON_EN_HAUTEUR = 0.38;

/** Où poser un galet : le centre du galet, en part de la largeur et de la hauteur de la carte (0 à 1). */
export interface PlaceSurLaCarte {
  x: number;
  y: number;
}

/**
 * Les places de `n` galets autour de « Toi », calculées à l'affichage et jamais gardées : un
 * cercle (une ellipse à la forme de la carte), le premier en haut, puis dans le sens des aiguilles
 * d'une montre, à intervalles égaux.
 */
export function placesEnCercle(n: number): PlaceSurLaCarte[] {
  return Array.from({ length: n }, (_, i) => {
    const angle = -Math.PI / 2 + (2 * Math.PI * i) / n;
    return {
      x: 0.5 + RAYON_EN_LARGEUR * Math.cos(angle),
      y: 0.5 + RAYON_EN_HAUTEUR * Math.sin(angle),
    };
  });
}

/** Les premières lignes d'une liste déjà rangée par le serveur. */
export function troisAuPlus<L>(lignes: readonly L[]): L[] {
  return lignes.slice(0, LIGNES_PAR_BLOC);
}

export type EvenementDeLAgenda = SemaineAgenda['evenements'][number];

/**
 * « Ça arrive » : les rendez-vous et les échéances de l'Agenda, d'aujourd'hui aux six jours
 * suivants, par jour puis par heure, trois au plus. Les attentes n'y sont pas : elles sont dans
 * « J'attends ».
 */
export function ceQuiArrive(semaines: readonly SemaineAgenda[], aujourdhui: string): EvenementDeLAgenda[] {
  const dernier = decaler(aujourdhui, 6);
  const vus = new Set<string>();
  const dedans: EvenementDeLAgenda[] = [];
  for (const semaine of semaines) {
    for (const e of semaine.evenements) {
      const cle = `${e.bulleId}|${e.nature}`;
      if (e.nature === 'attente' || e.jour < aujourdhui || e.jour > dernier || vus.has(cle)) continue;
      vus.add(cle);
      dedans.push(e);
    }
  }
  dedans.sort((a, b) => a.jour.localeCompare(b.jour) || (a.debut ?? '').localeCompare(b.debut ?? ''));
  return troisAuPlus(dedans);
}
